import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Calendar } from '../../components/ui/calendar';
import { toast } from 'sonner';
import {
  Clock,
  Video,
  MapPin,
  Phone,
  CheckCircle2,
  XCircle,
  Calendar as CalendarIcon,
  Users
} from 'lucide-react';

export default function DoctorAppointments() {
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);

  useEffect(() => {
    loadAppointments();
  }, [selectedDate]);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      setTodayAppointments([
        { id: 1, patient: 'John Smith', type: 'Video Consultation', time: '10:00 AM', duration: '30 min', status: 'Confirmed', condition: 'Post-Op Follow-up' },
        { id: 2, patient: 'Maria Garcia', type: 'In-Person', time: '11:30 AM', duration: '45 min', status: 'Confirmed', condition: 'Initial Consultation' },
        { id: 3, patient: 'Ahmed Hassan', type: 'Video Consultation', time: '2:00 PM', duration: '30 min', status: 'Pending', condition: 'Treatment Discussion' },
        { id: 4, patient: 'Sarah Johnson', type: 'Phone Call', time: '3:30 PM', duration: '15 min', status: 'Confirmed', condition: 'Test Results' },
        { id: 5, patient: 'Li Wei', type: 'In-Person', time: '4:30 PM', duration: '60 min', status: 'Confirmed', condition: 'Pre-Surgery Assessment' },
      ]);

      setAppointments([
        { date: '2024-01-20', count: 3 },
        { date: '2024-01-21', count: 5 },
        { date: '2024-01-22', count: 2 },
        { date: '2024-01-23', count: 4 },
      ]);
    } catch (error) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Video Consultation': return <Video className="w-4 h-4 text-blue-500" />;
      case 'In-Person': return <MapPin className="w-4 h-4 text-green-500" />;
      case 'Phone Call': return <Phone className="w-4 h-4 text-amber-500" />;
      default: return <CalendarIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed': return 'bg-green-100 text-green-700 border-green-200';
      case 'Pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Cancelled': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="doctor-appointments">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Appointments</h1>
          <p className="text-gray-500 text-sm mt-1">Manage your consultation schedule</p>
        </div>
        <Button className="bg-teal-600 hover:bg-teal-700">
          <CalendarIcon className="w-4 h-4 mr-2" /> Block Time Slot
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="border shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Calendar</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border"
            />
          </CardContent>
        </Card>

        {/* Today's Appointments */}
        <Card className="lg:col-span-2 border shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Today's Schedule</CardTitle>
                <CardDescription>{todayAppointments.length} appointments</CardDescription>
              </div>
              <Badge className="bg-teal-100 text-teal-700 border-teal-200">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
              </div>
            ) : todayAppointments.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No appointments scheduled for today</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todayAppointments.map((apt) => (
                  <div key={apt.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-shrink-0 w-20 text-center">
                      <p className="text-lg font-bold text-gray-900">{apt.time}</p>
                      <p className="text-xs text-gray-500">{apt.duration}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-teal-100 text-teal-700">
                          {apt.patient.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{apt.patient}</p>
                        {getTypeIcon(apt.type)}
                        <span className="text-xs text-gray-500">{apt.type}</span>
                      </div>
                      <p className="text-sm text-gray-500">{apt.condition}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${getStatusColor(apt.status)} border`}>
                        {apt.status}
                      </Badge>
                      {apt.type === 'Video Consultation' && apt.status === 'Confirmed' && (
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                          <Video className="w-4 h-4 mr-1" /> Join
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Today</p>
                <p className="text-2xl font-bold text-gray-900">{todayAppointments.length}</p>
              </div>
              <CalendarIcon className="w-8 h-8 text-teal-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">This Week</p>
                <p className="text-2xl font-bold text-gray-900">18</p>
              </div>
              <CalendarIcon className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Confirmed</p>
                <p className="text-2xl font-bold text-gray-900">{todayAppointments.filter(a => a.status === 'Confirmed').length}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{todayAppointments.filter(a => a.status === 'Pending').length}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
